import React from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'

import { Modal, Empty, useObjectMemo } from '@pepper/ui'

import styles from './ModalWrapper.module.css'

type ModalContextValue = {
  removeModal: () => void
  isModalVisible: boolean
}

type ModalWrapperProps = {
  modalId?: string
  onCancel?: () => void
  className?: string
  width?: string | number
  closable?: boolean
  destroyOnClose?: boolean
}

const ModalContext = React.createContext<ModalContextValue | null>(null)

export const ModalWrapper = NiceModal.create<ModalWrapperProps>(
  ({
    onCancel,
    children,
    className = '',
    width,
    modalId,
    closable = true,
    destroyOnClose,
  }) => {
    const { visible: isModalVisible, remove: removeModal } = useModal(
      modalId ?? ''
    )

    const modalContextValue = useObjectMemo({ isModalVisible, removeModal })
    return (
      <ModalContext.Provider value={modalContextValue}>
        <Modal
          wrapClassName={clsx(width ? '' : styles.modalWrapper, className)}
          title={null}
          footer={null}
          visible={isModalVisible}
          closable={closable}
          onCancel={() => {
            removeModal()
            onCancel?.()
          }}
          width={width}
          destroyOnClose={destroyOnClose}
        >
          {children}
        </Modal>
      </ModalContext.Provider>
    )
  }
)

function useModalContext() {
  const context = React.useContext(ModalContext)
  if (!context) {
    throw new Error(
      'ModalWrapper compound components cannot be rendered outside the ModalWrapper component'
    )
  }
  return context
}

type ChildrenFunction = (values: ModalContextValue) => React.ReactNode

export const ModalTitle = ({
  children,
}: {
  children: ChildrenFunction | React.ReactNode
}): JSX.Element => {
  const { isModalVisible, removeModal } = useModalContext()
  return (
    <div className={styles.modalTitle}>
      {children && typeof children === 'function'
        ? children({ isModalVisible, removeModal })
        : children}
    </div>
  )
}

export const ModalBody = ({
  children,
}: {
  children: ChildrenFunction | React.ReactNode
}): JSX.Element => {
  const { isModalVisible, removeModal } = useModalContext()
  if (children === undefined || children == null)
    return (
      <div className={styles.modalBody}>
        <div className={styles.empty}>
          <Empty description="No content." />
        </div>
      </div>
    )
  return (
    <div className={styles.modalBody}>
      {children && typeof children === 'function'
        ? children({ isModalVisible, removeModal })
        : children}
    </div>
  )
}

export const ModalFooter = ({
  children,
}: {
  children: ChildrenFunction | React.ReactNode
}): JSX.Element => {
  const { isModalVisible, removeModal } = useModalContext()
  return (
    <div className={styles.modalFooter}>
      {children && typeof children === 'function'
        ? children({ isModalVisible, removeModal })
        : children}
    </div>
  )
}

export const removeModalWrapper = () => NiceModal.remove('seo-headless-modal')
export const showModalWrapper = () => NiceModal.show('seo-headless-modal')
